'use client';

import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { DeliveryMethodType, Product, TailoredApiResponse } from '@libs/common-types';
import { getQuantityModel } from '../../product-details';
import { isMethodAvailableForPurchaseOption } from '../lib';
import type { MiniPdpForm } from '../types';
export type MiniPdpQuantityProps = Readonly<{
  product: Product;
  tailored: TailoredApiResponse['data'] | null;
  isLoading: boolean;
  isError?: boolean;
}>;
export function useMiniPdpQuantity({
  product,
  tailored,
  isLoading,
  isError = false
}: MiniPdpQuantityProps) {
  const {
    formState,
    setValue,
    setError,
    clearErrors
  } = useFormContext<MiniPdpForm>();
  const fulfilmentMethod = useWatch({
    name: 'fulfilmentMethod'
  });
  const selectedSku = useWatch({
    name: 'selectedSku'
  });
  const quantity = useWatch({
    name: 'quantity'
  });
  const purchaseOption = useWatch({
    name: 'purchaseOption'
  });
  const method = fulfilmentMethod.toUpperCase() as DeliveryMethodType;
  const disabled = isLoading || formState.isSubmitting;
  const quantityModel = useMemo(() => {
    const isMethodAvailable = isMethodAvailableForPurchaseOption(product, method, purchaseOption);
    return getQuantityModel({
      product,
      tailored,
      sku: selectedSku,
      method,
      quantity,
      isMethodAvailable
    });
  }, [fulfilmentMethod, product, purchaseOption, quantity, selectedSku, tailored]);
  const {
    items,
    minQty,
    stock,
    fallback,
    shouldRender
  } = quantityModel;
  useEffect(() => {
    if (isError || !selectedSku) return;
    if (!items.length) {
      setError('quantity', {
        message: 'No quantity items available'
      });
      return;
    }
    clearErrors('quantity');
    setValue('quantity', minQty);
  }, [clearErrors, fulfilmentMethod, isError, items.length, minQty, selectedSku, setError, setValue, stock]);
  useEffect(() => {
    if (isError || !selectedSku || !items.length) return;
    if (fallback !== quantity) setValue('quantity', fallback);
  }, [fallback, isError, quantity, selectedSku, setValue, items.length]);
  return {
    items,
    fulfilmentMethod,
    selectedSku,
    disabled,
    shouldRender: shouldRender && !isError
  };
}